import {Injectable} from '@angular/core';

import {I18NextPipe} from 'angular-i18next';
import {gql} from 'apollo-angular';

import {DimensionList, KnownMeasure, TimeBucket, timeDimensionOptions} from 'app/features/analytics/features/reports/dimension';
import {Label} from 'app/features/analytics/features/reports/reports.interface';

import {
    DimensionGroup,
    DimensionProps,
    Measure,
    MeasuresGroup,
    MetricId,
    UaMeasure,
} from 'app/features/analytics/service/dimensions-and-measures.interfaces';
import {MeasureToAdd, SelectedMeasure, TimeGroup} from 'app/features/analytics/service/filter-value.interface';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {Dimension, HavingFilterOperator} from 'src/globalTypes';
import {availableMeasures, availableMeasures_analytics_availableMeasures} from 'app/features/analytics/service/gql/availableMeasures';
import {isNumeric} from 'app/features/analytics/features/reports/components/reports-page/query-params.parse';
import {map} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';

const availableMeasuresQuery = gql`
    query availableMeasures {
        analytics {
            availableMeasures {
                id
                label
                shortLabel
                supportedDimensions
                fullExcludeDimensions
                groupName
                ... on UaMeasure {
                    forecast
                    availableDays
                }
                __typename
            }
        }
    }
`;

export interface AvailableMeasuresAndDimension {
    measuresGroup: MeasuresGroup[];
    measureList: Measure[];
    filtersList: Label[];
    groupByList: Label[];
    dimensionGroup: Map<string, DimensionGroup>;
}

enum DimensionGroupName {
    MONETIZATION = 'Monetization',
    BID_AUTOMATION = 'Bid Automation',
    ATTRIBUTION = 'Attribution',
    COMMON = 'Common',
}

@Injectable({
    providedIn: 'root',
})
export class DimensionsAndMeasuresService {
    subTotalsSortSettings$: BehaviorSubject<Map<string, string>> = new BehaviorSubject(new Map<string, string>());

    constructor(
        private api: ApiService,
        protected i18Next: I18NextPipe
    ) {}

    onlyInstallDateMetrics: Measure[];

    available: AvailableMeasuresAndDimension;

    metricsMap: Record<MetricId, Measure>;
    dimensionMap: Record<Dimension, DimensionProps>;

    dateIndependentDimensions = [
        KnownMeasure.Spend,
        KnownMeasure.Cpi,
        KnownMeasure.CostPerNetworkInstall,
        KnownMeasure.Installs,
        KnownMeasure.NetworkInstalls,
        KnownMeasure.CtrUA,
        KnownMeasure.ClicksUA,
        KnownMeasure.ImpressionsUA,
    ];

    deprecatedDimensionsMap = {
        [Dimension.ATTRIBUTION_NETWORK]: Dimension.ATTRIBUTION_NETWORK_HID,
        [Dimension.ATTRIBUTION_CAMPAIGN]: Dimension.ATTRIBUTION_CAMPAIGN_HID,
        [Dimension.ATTRIBUTION_AD_SET]: Dimension.ATTRIBUTION_AD_SET_HID,
    };

    isKnownMeasure(m: MetricId): boolean {
        return Boolean(this.metricsMap[m]);
    }

    isKnownDimension(m: Dimension | string): boolean {
        return Boolean(this.dimensionMap[m as Dimension]);
    }

    isUAMeasure(id: string): boolean {
        return this.metricsMap[id].__typename === 'UaMeasure';
    }

    isOneDateMeasure(measure: Measure | SelectedMeasure): boolean {
        if ('__typename' in measure && measure.__typename === 'UaMeasure') {
            return false;
        }

        return [
            KnownMeasure.ProfitLoss,
            KnownMeasure.ProfitLossWithAgencyProfitShare,
            KnownMeasure.TotalRevenueCalendar,
            KnownMeasure.TotalRevenueCohort,
            KnownMeasure.AgencyProfitShareCalendar,
            KnownMeasure.AgencyProfitShareCohort,
            KnownMeasure.MediaProfitCalendar,
            KnownMeasure.MediaProfitCohort,
            KnownMeasure.MediaRoasCalendar,
            KnownMeasure.MediaRoasCohort,
            KnownMeasure.MarketingCostsCalendar,
            KnownMeasure.MarketingCostsCohort,
            KnownMeasure.MarketingProfitCalendar,
            KnownMeasure.MarketingProfitCohort,
            KnownMeasure.ChardonnayExpensesCalendar,
            KnownMeasure.ChardonnayExpensesCohort,
            KnownMeasure.ChardonnayProfitCalendar,
            KnownMeasure.ChardonnayProfitCohort,
            KnownMeasure.ChardonnayRoiCalendar,
            KnownMeasure.ChardonnayRoiCohort,
            KnownMeasure.RomiCalendar,
            KnownMeasure.RomiCohort,
            KnownMeasure.WhiteLabelGrossProfitWithApsCalendar,
            KnownMeasure.WhiteLabelGrossProfitWithApsCohort,
            KnownMeasure.WhiteLabelProfitShareWithApsCalendar,
            KnownMeasure.WhiteLabelProfitShareWithApsCohort,
            KnownMeasure.WhiteLabelProfitShareWithApsCalendar,
            KnownMeasure.WhiteLabelProfitShareWithApsCohort,
            KnownMeasure.InstallsDc,
            KnownMeasure.EventRatio,
            KnownMeasure.EventConversion,
            KnownMeasure.eInstalls,
        ].includes(measure.id as KnownMeasure);
    }

    canActivate(): Promise<boolean> {
        return this.resolve().then(() => true);
    }

    resolve(): Promise<any> {
        return this.fetch()
            .toPromise()
            .then(measures => {
                this.handleAvailableMeasures(measures);
            });
    }

    hackMeasures(measures: Measure[], hackMeasures = (m: Measure) => m): Measure[] {
        const weight = (measure: Measure) => {
            switch (measure.groupName) {
                case 'Measure':
                    return 3;
                default:
                    return 0;
            }
        };

        return measures
            .map(m => {
                const result = {...m};

                // skip
                if (result.id.includes('subscription')) {
                    return;
                }

                // skip
                if (result.id === 'retention_rate_forecast') {
                    return;
                }

                // rename
                if (m.label.includes('Cumulative Revenue') && result.id !== 'ios_cumulative_revenue') {
                    // Cumulative Revenue Forecast D1 -> Revenue D1 Forecast
                    result.shortLabel = 'Revenue';
                }

                if (m.shortLabel && m.shortLabel.includes('SCARP')) {
                    result.shortLabel = result.shortLabel.replace('SCARP', 'Sub CARP');
                }

                if (m.shortLabel && m.shortLabel.includes('CARPU')) {
                    result.shortLabel = result.shortLabel.replace('CARPU', ' ARPU');
                }

                if (m.shortLabel && m.shortLabel.includes('CARPPU')) {
                    result.shortLabel = result.shortLabel.replace('CARPPU', ' ARPPU');
                }

                if (m.label.match(/Avg(.*)Per Month/i)) {
                    result.shortLabel = result.label.replace(/Avg (.*) Per Month/i, 'Monthly $1');
                }

                if (m.label.match(/Avg(.*)Per Day/i)) {
                    result.shortLabel = result.label.replace(/Avg (.*) Per Day/i, 'Daily $1');
                }

                let label = result.shortLabel || result.label;
                if (label.match(/^Avg/i)) {
                    result.shortLabel = label.substring(3);
                }

                const labelKey = ['analytics.measures.label', m.id].join('.');
                if (labelKey !== this.i18Next.transform(labelKey)) {
                    result.label = this.i18Next.transform(labelKey);
                    result.shortLabel = null;
                }
                const shortLabelKey = ['analytics.measures.shortLabel', m.id].join('.');
                if (shortLabelKey !== this.i18Next.transform(shortLabelKey)) {
                    result.shortLabel = this.i18Next.transform(shortLabelKey);
                }

                // fix
                if (m.__typename === 'UaMeasure' && !result.supportedDimensions.includes(Dimension.SEGMENT)) {
                    result.supportedDimensions = [...result.supportedDimensions, Dimension.SEGMENT];
                }

                if (
                    [KnownMeasure.SessionLength, KnownMeasure.AvgTimeSpentPerUser].includes(m.id as KnownMeasure) &&
                    result.supportedDimensions.includes(Dimension.AD_TYPE)
                ) {
                    result.supportedDimensions = result.supportedDimensions.filter(d => d !== Dimension.AD_TYPE);
                }

                if (
                    [
                        KnownMeasure.AvgTimeSpentPerUser,
                        KnownMeasure.AvgTimeSpentPerUserDaily,
                        KnownMeasure.AvgTimeSpentPerUserMonthly,
                        KnownMeasure.TotalRevenue,
                        KnownMeasure.IosTotalRevenue,
                        KnownMeasure.Installs,
                    ].includes(m.id as KnownMeasure) &&
                    !result.supportedDimensions.includes(Dimension.SEGMENT)
                ) {
                    result.supportedDimensions = [...result.supportedDimensions, Dimension.SEGMENT];
                }

                if (
                    [KnownMeasure.Profit, KnownMeasure.ProfitForecast, KnownMeasure.Roas, KnownMeasure.RoasForecast].includes(
                        m.id as KnownMeasure
                    ) &&
                    result.supportedDimensions.includes(Dimension.SEGMENT)
                ) {
                    result.supportedDimensions = result.supportedDimensions.filter(d => d !== Dimension.SEGMENT);
                }

                if (!result.supportedDimensions.includes(Dimension.APP)) {
                    result.supportedDimensions = [...result.supportedDimensions, Dimension.APP];
                }

                // if (m.id === KnownMeasure.AvgTimeSpentPerUser && !m.supportedDimensions.includes(Dimension.DATE)) {
                //     result.supportedDimensions = [...result.supportedDimensions, Dimension.DATE];
                // }

                if (this.onlyInstallDateMetrics.some(metric => metric.id === result.id)) {
                    result.supportedDimensions = [...result.supportedDimensions, Dimension.DATE];
                }

                if (KnownMeasure.ImpressionsUA === result.id) {
                    result.supportedDimensions = result.supportedDimensions.filter(s => s !== Dimension.SEGMENT);
                }

                result.supportedDimensions = [...new Set(result.supportedDimensions)];

                return result;
            })
            .map(hackMeasures)
            .filter(Boolean)
            .sort((a, b) => weight(b) - weight(a));
    }

    handleAvailableMeasures(measures: availableMeasures_analytics_availableMeasures[], hackMeasures?) {
        this.onlyInstallDateMetrics = measures.filter(m => !m.supportedDimensions.includes(Dimension.DATE) || m.__typename === 'UaMeasure');
        const roas = measures.find(m => m.id === KnownMeasure.RoasActual);
        if (roas && !this.onlyInstallDateMetrics.some(m => m.id === KnownMeasure.RoasActual)) {
            this.onlyInstallDateMetrics.push(roas);
        }

        console.debug('[DMS] onlyInstallDateMetrics', this.onlyInstallDateMetrics);
        const allMeasures = this.hackMeasures([...measures], hackMeasures);
        this.dimensionMap = {} as Record<Dimension, DimensionProps>;
        this.metricsMap = {} as Record<any, any>;

        this.buildMap(allMeasures, this.metricsMap);
        this.available = this.buildLists(allMeasures);
    }

    buildMap(measures: Measure[], metricsMap: Record<MetricId, Measure> = {}): Record<MetricId, Measure> {
        measures.forEach(m => {
            metricsMap[m.id] = m;
            m.supportedDimensions.forEach(d => this.addMeasureToDimension(d, m));
        });
        return metricsMap;
    }

    buildLists(measureList: Measure[]): AvailableMeasuresAndDimension {
        const groupsMap = measureList.reduce(function (accum, currentVal) {
            if (!accum.has(currentVal.groupName)) {
                accum.set(currentVal.groupName, {
                    groupName: currentVal.groupName,
                    measureList: [],
                });
            }
            accum.get(currentVal.groupName).measureList.push(currentVal);

            return accum;
        }, new Map());

        const measuresGroup = [...groupsMap.values()];

        const filtersList = Object.values(this.dimensionMap)
            .filter(f => !this.deprecatedDimensionsMap[f.id])
            .map(d => ({
                id: <any>d.id,
                label: d.label,
            }));

        const weight = (d: string) => {
            switch (d) {
                case Dimension.DATE:
                    return 3;
                case Dimension.INSTALL_DATE:
                    return 2;
                default:
                    return 0;
            }
        };

        const groupByList = Object.values(this.dimensionMap)
            .map(d => {
                const newGroupBy: any = {
                    id: <any>d.id,
                    label: this.i18Next.transform('analytics.dimensions.' + d.id),
                    groupName: this.buildGroupNameDimension(d.id),
                };

                if (newGroupBy.id === Dimension.DATE || newGroupBy.id === Dimension.INSTALL_DATE) {
                    newGroupBy.timeBucket = TimeBucket.DAY;
                }

                return newGroupBy;
            })
            .filter(group => !this.deprecatedDimensionsMap[group.id])
            .sort((a, b) => weight(b.id) - weight(a.id));

        const dimensionGroup: Map<string, DimensionGroup> = groupByList.reduce((accum, currentVal) => {
            if (!accum.has(currentVal.groupName)) {
                accum.set(currentVal.groupName, {
                    groupName: currentVal.groupName,
                    dimensionList: [],
                });
            }

            accum.get(currentVal.groupName).dimensionList.push(currentVal);

            return accum;
        }, new Map());

        return {
            measureList,
            measuresGroup,
            filtersList,
            groupByList,
            dimensionGroup,
        };
    }

    buildGroupNameDimension(id: string) {
        switch (id) {
            case Dimension.AD_TYPE:
            case Dimension.DISPLAY_AD_TYPE:
            case Dimension.AD_NETWORK:
            case Dimension.PLACEMENT:
            case Dimension.LINE_ITEM:
            case Dimension.SDK_VERSION:
            case Dimension.FRAMEWORK:
            case Dimension.PLUGIN_VERSION:
            case Dimension.NETWORK_ACCOUNT:
            case Dimension.ZERO_IDFA:
            case Dimension.PARALLEL_BIDDING:
            case Dimension.POST_BID:
            case Dimension.SEGMENT:
                return DimensionGroupName.MONETIZATION;
            case Dimension.HIST_AD_SET_AUTO_BID:
            case Dimension.HIST_AD_SET_PAYBACK_PERIOD:
            case Dimension.HIST_AD_SET_STATUS:
            case Dimension.HIST_CAMPAIGN_AUTO_BID:
            case Dimension.HIST_CAMPAIGN_PAYBACK_PERIOD:
            case Dimension.HIST_CAMPAIGN_STATUS:
            case Dimension.HIST_CAMPAIGN_TYPE:
            case Dimension.HIST_COUNTRY_AUTO_BID:
            case Dimension.HIST_COUNTRY_PAYBACK_PERIOD:
            case Dimension.HIST_COUNTRY_STATUS:
            case Dimension.HIST_SOURCE_APP_AUTO_BID:
            case Dimension.HIST_SOURCE_APP_PAYBACK_PERIOD:
            case Dimension.HIST_SOURCE_APP_STATUS:
            case Dimension.AUTO_BID:
                return DimensionGroupName.BID_AUTOMATION;
            case Dimension.UA_STATUS:
            case Dimension.MMP:
            case Dimension.ZERO_MMP_ID:
            case Dimension.ATTRIBUTION_AD_SET:
            case Dimension.ATTRIBUTION_AD_SET_HID:
            case Dimension.ATTRIBUTION_CAMPAIGN:
            case Dimension.ATTRIBUTION_CAMPAIGN_HID:
            case Dimension.ATTRIBUTION_CREATIVE:
            case Dimension.ATTRIBUTION_CREATIVE_HID:
            case Dimension.ATTRIBUTION_NETWORK:
            case Dimension.ATTRIBUTION_NETWORK_HID:
            case Dimension.ATTRIBUTION_PARTNER:
            case Dimension.ATTRIBUTION_PARTNER_SOURCE:
            case Dimension.ATTRIBUTION_SOURCE:
            case Dimension.ATTRIBUTION_SOURCE_APP:
            case Dimension.WHITE_LABEL_PUBLISHER:
            case Dimension.IS_ORGANIC:
            case Dimension.ATTRIBUTION_SEARCH_KEYWORDS:
            case Dimension.ATTRIBUTION_SEARCH_KEYWORDS_IDS:
            case Dimension.STUDIO:
            case Dimension.REVENUE_MANAGER:
            case Dimension.ATTRIBUTION_AGENCY:
            case Dimension.IS_CLAIMED_BY_PUBLISHER:
                return DimensionGroupName.ATTRIBUTION;
            default:
                return DimensionGroupName.COMMON;
        }
    }

    addMeasureToDimension(d: Dimension | string, m: Measure) {
        if (!DimensionList.some(dd => dd.id === d)) {
            console.warn('Unknown Dimension', d);
            return;
        }

        if (!this.dimensionMap[d]) {
            this.dimensionMap[d] = <DimensionProps>{
                id: d,
                label: this.i18Next.transform('analytics.dimensions.' + d),
                supportedMeasures: [],
                groupName: d,
            };
        }
        this.dimensionMap[d].supportedMeasures.push(m.id);
    }

    fetch() {
        return this.api
            .query<availableMeasures>({
                query: availableMeasuresQuery,
                cacheHours: 1,
            })
            .pipe(map(r => r.analytics.availableMeasures));
    }

    toSelectedMeasure = (toAddMeasure: MeasureToAdd | string): SelectedMeasure => {
        if (typeof toAddMeasure === 'string') {
            toAddMeasure = {id: toAddMeasure, day: null};
        }

        if (!this.isKnownMeasure(toAddMeasure.id)) {
            return null;
        }

        const having =
            toAddMeasure.having && typeof toAddMeasure.having === 'object'
                ? Object.entries(toAddMeasure.having)
                      // remove unknown keys
                      // remove not numeric values
                      .filter(
                          ([key, value]) =>
                              Object.keys(HavingFilterOperator).includes(key) && (isNumeric(value as string) || typeof value === 'number')
                      )
                      .reduce(
                          (acc, [key, value]) => {
                              acc[key as HavingFilterOperator] = Number(value);
                              return acc;
                          },
                          {} as Record<HavingFilterOperator, number>
                      )
                : undefined;

        const selectedMeasure: SelectedMeasure = {
            id: toAddMeasure.id,
            day: toAddMeasure.day,
            having,
            label: this.metricsMap[toAddMeasure.id].shortLabel
                ? this.metricsMap[toAddMeasure.id].shortLabel
                : this.metricsMap[toAddMeasure.id].label,
        };

        if (this.isUAMeasure(selectedMeasure.id)) {
            if (this.metricsMap[selectedMeasure.id].shortLabel) {
                const suffix = (this.metricsMap[selectedMeasure.id] as UaMeasure).forecast ? 'Forecast' : 'To-Date';
                selectedMeasure.label = `${this.metricsMap[selectedMeasure.id].shortLabel} D${selectedMeasure.day} ${suffix}`;
            } else {
                selectedMeasure.label = `${this.metricsMap[selectedMeasure.id].label} D${selectedMeasure.day}`;
            }
            if (selectedMeasure.day === undefined || typeof selectedMeasure.day !== 'number') {
                return null;
            }
        } else {
            selectedMeasure.day = null;
        }

        return selectedMeasure;
    };

    getHeaderName(header) {
        switch (header.id) {
            case Dimension.DATE:
                return 'Activity ' + timeDimensionOptions.find(opt => opt.value === (<TimeGroup>header).timeBucket).label;
            case Dimension.INSTALL_DATE:
                return 'Install ' + timeDimensionOptions.find(opt => opt.value === (<TimeGroup>header).timeBucket).label;
            case Dimension.BIDMANAGER_CREATIVE_HID:
                return 'Name';

            default:
                return header.label || this.dimensionMap[header.id]?.label || this.metricsMap[header.id]?.label;
        }
    }
}
